/**
 * Icon component for rendering pre-imported SVG icons with consistent styling and props.
 *
 * @module Icon
 * @description
 * This component provides a simple way to display SVG icons in your application by name.
 * Only the icons explicitly imported and registered in the `icons` object will be available.
 *
 * @example
 * // Render a check icon at size 32px
 * <Icon name="circle-check" size={32} className="text-green-500" />
 *
 * // Render a warning icon with default size (24px)
 * <Icon name="triangle-exclamation" />
 */

import { icons } from "./Icon.constants"
import { IconName, IconProps } from "./Icon.types"

/**
 * Icon component props.
 * @typedef {Object} IconProps
 * @property {IconName} name - The name of the icon to display (must match a key in `icons`).
 * @property {number} [size=24] - The size (width and height) of the icon in pixels.
 * @property {string} [className] - Additional Tailwind or CSS classes to apply to the icon.
 * @property {Object} [style] - Inline styles to apply to the icon (overrides size if width/height specified).
 * @property {Object} [props] - Any other props will be passed to the SVG element.
 */

/**
 * Renders an SVG icon by name, with customizable size and styling.
 *
 * @param {IconProps} props - The icon properties.
 * @returns {JSX.Element|null} The icon component, or null if the name is invalid.
 */
export const Icon = ({ name, size = 24, className = "", style, ...props }: IconProps) => {
  const Comp = icons[name]
  if (!Comp) return null
  // Forward `size` to the underlying icon library — it renders width/height
  // as SVG attributes, which any caller CSS rule (e.g. `[&_svg]:size-4` inside
  // NxButton) can override without `!important`. Inline `style` would beat
  // class-based rules by specificity, so we no longer set width/height there.
  return <Comp className={className} size={size} style={style} {...props} />
}

export function createIconComponent(name: IconName, size = 12) {
  return function IconComponent(props: { className?: string }) {
    return <Icon name={name} size={size} {...props} />
  }
}
